import { useMemo } from "react"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import { useAudienceClassificationControllerGetAllQuery } from "~/ui-rtk/api/audienceClassificationApi"
import { usePeriod } from "~/ui-rtk/hooks/period"

export type TCategory = "cold" | "warm" | "retention" | "needs_review"

export type TAdsetAuditRow = {
  adsetId: string
  adsetName: string
  campaignName: string
  spend: number
  category: TCategory
  excludedAudiences: { id: string; name: string }[]
  missingExclusions: { id: string; name: string }[]
}

export function computeCategory(
  excludedIds: Set<string>,
  engagedIds: string[],
  existingIds: string[],
): TCategory {
  // Can't auto-detect without classified audiences
  if (engagedIds.length === 0 && existingIds.length === 0) return "needs_review"
  // No exclusions set on this adset — can't determine intent
  if (excludedIds.size === 0) return "needs_review"
  if (engagedIds.some(id => excludedIds.has(id))) return "cold"
  if (existingIds.some(id => excludedIds.has(id))) return "warm"
  // Has exclusions but none match classified audiences
  return "needs_review"
}

export function computeMissingExclusions(
  category: TCategory,
  excludedIds: Set<string>,
  engagedAudiences: { id: string; name: string }[],
  existingAudiences: { id: string; name: string }[],
): { id: string; name: string }[] {
  switch (category) {
    case "cold":
      return [...engagedAudiences, ...existingAudiences].filter(
        a => !excludedIds.has(a.id),
      )
    case "warm":
      return existingAudiences.filter(a => !excludedIds.has(a.id))
    case "retention":
    case "needs_review":
      return []
  }
}

export default function useConnect() {
  const defaultDates = useMemo((): [Date, Date] => {
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    return [thirtyDaysAgo, new Date()]
  }, [])

  const { period, setPeriod } = usePeriod(
    undefined,
    false,
    undefined,
    defaultDates,
  )

  const { data: classifications, isLoading: isLoadingClassifications } =
    useAudienceClassificationControllerGetAllQuery()

  const { resultSet: audienceResultSet, isLoading: isLoadingAudienceNames } =
    useCubeQuery({
      dimensions: [
        "company_custom_audiences.audience_id",
        "company_custom_audiences.audience_name",
      ],
    })

  const audienceNameMap = useMemo(() => {
    if (!audienceResultSet) return new Map<string, string>()
    const map = new Map<string, string>()
    for (const row of audienceResultSet.rawData() as Record<
      string,
      unknown
    >[]) {
      const id = String(row["company_custom_audiences.audience_id"] ?? "")
      const name = String(row["company_custom_audiences.audience_name"] ?? "")
      if (id) map.set(id, name)
    }
    return map
  }, [audienceResultSet])

  const engagedAudiences = useMemo(() => {
    if (!classifications) return []
    return classifications
      .filter(c => c.classification === "ENGAGED")
      .map(c => ({
        id: c.audienceRef,
        name: audienceNameMap.get(c.audienceRef) ?? c.audienceRef,
      }))
  }, [classifications, audienceNameMap])

  const existingAudiences = useMemo(() => {
    if (!classifications) return []
    return classifications
      .filter(c => c.classification === "EXISTING")
      .map(c => ({
        id: c.audienceRef,
        name: audienceNameMap.get(c.audienceRef) ?? c.audienceRef,
      }))
  }, [classifications, audienceNameMap])

  const hasDefinedAudiences =
    engagedAudiences.length > 0 && existingAudiences.length > 0

  const { resultSet: adsetResultSet, isLoading: isLoadingAdsets } =
    useCubeQuery({
      dimensions: [
        "company_media_metrics_adsets.adset_id",
        "company_media_metrics_adsets.adset_name",
        "company_media_metrics_adsets.campaign_name",
        "company_media_metrics_adsets.targeting_excluded_custom_audiences",
        "company_media_metrics_adsets.media_channel",
      ],
      measures: ["company_media_metrics_adsets.spend"],
      timeDimensions: [
        {
          dimension: "company_media_metrics_adsets.md_date",
          dateRange: period as [string, string],
        },
      ],
      filters: [
        {
          member: "company_media_metrics_adsets.media_channel",
          operator: "equals",
          values: ["meta"],
        },
        {
          member: "company_media_metrics_adsets.spend",
          operator: "gt",
          values: ["0"],
        },
      ],
    })

  const adsetRows: TAdsetAuditRow[] = useMemo(() => {
    if (!adsetResultSet) return []

    const engagedIds = engagedAudiences.map(a => a.id)
    const existingIds = existingAudiences.map(a => a.id)

    return (adsetResultSet.rawData() as Record<string, unknown>[]).map(row => {
      const adsetId = String(row["company_media_metrics_adsets.adset_id"] ?? "")
      const adsetName = String(
        row["company_media_metrics_adsets.adset_name"] ?? "",
      )
      const campaignName = String(
        row["company_media_metrics_adsets.campaign_name"] ?? "",
      )
      const spend = Number(row["company_media_metrics_adsets.spend"] ?? 0)
      const rawExclusions =
        row["company_media_metrics_adsets.targeting_excluded_custom_audiences"]

      let excludedAudiences: { id: string; name: string }[] = []
      if (rawExclusions && typeof rawExclusions === "string") {
        try {
          const parsed = JSON.parse(rawExclusions)
          if (Array.isArray(parsed)) {
            excludedAudiences = parsed.map(
              (item: { id?: string; name?: string }) => ({
                id: String(item.id ?? ""),
                name: String(item.name ?? ""),
              }),
            )
          }
        } catch {
          // Invalid JSON — treat as no exclusions
        }
      }

      const excludedIds = new Set(excludedAudiences.map(a => a.id))
      const category = computeCategory(excludedIds, engagedIds, existingIds)
      const missingExclusions = computeMissingExclusions(
        category,
        excludedIds,
        engagedAudiences,
        existingAudiences,
      )

      return {
        adsetId,
        adsetName,
        campaignName,
        spend,
        category,
        excludedAudiences,
        missingExclusions,
      }
    })
  }, [adsetResultSet, engagedAudiences, existingAudiences])

  return {
    hasDefinedAudiences,
    isLoading:
      isLoadingClassifications || isLoadingAudienceNames || isLoadingAdsets,
    adsetRows,
    engagedAudiences,
    existingAudiences,
    period,
    setPeriod,
  }
}
